import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2Icon, PlusIcon, SearchIcon } from 'lucide-react';
import { buttonClasses } from '../../components/ui/Button';
import { QuoteStatusBadge } from '../../components/admin/QuoteStatusBadge';
import { useAdminData } from '../../contexts/AdminDataContext';
import { formatDate } from '../../utils/format';
import { effectiveStatus, formatKr } from '../../utils/quote';
import { quoteStatuses } from '../../types/quote';
import type { QuoteStatus } from '../../types/quote';
type Filter = 'Alle' | QuoteStatus;
export function Quotes() {
  const {
    quotes,
    dataLoading
  } = useAdminData();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('Alle');
  const withStatus = useMemo(() => quotes.map(quote => ({
    quote,
    status: effectiveStatus(quote)
  })), [quotes]);
  const summary = useMemo(() => ({
    Kladde: withStatus.filter(item => item.status === 'Kladde').length,
    Sendt: withStatus.filter(item => item.status === 'Sendt').length,
    Accepteret: withStatus.filter(item => item.status === 'Accepteret').length,
    Afvist: withStatus.filter(item => item.status === 'Afvist').length,
    Udløbet: withStatus.filter(item => item.status === 'Udløbet').length
  }), [withStatus]);
  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    return withStatus.filter(({
      quote,
      status
    }) => {
      const matchesFilter = filter === 'Alle' || status === filter;
      const haystack = `${quote.quoteNumber} ${quote.customerName} ${quote.title}`.toLowerCase();
      return matchesFilter && (!term || haystack.includes(term));
    });
  }, [filter, query, withStatus]);
  const cards: {
    label: string;
    value: number;
  }[] = [{
    label: 'Kladder',
    value: summary.Kladde
  }, {
    label: 'Sendt',
    value: summary.Sendt
  }, {
    label: 'Accepteret',
    value: summary.Accepteret
  }, {
    label: 'Afvist',
    value: summary.Afvist
  }, {
    label: 'Udløbet',
    value: summary.Udløbet
  }];
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-1034" data-mp="1ioi1vx703w7u">
      <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-1035" data-mp="1nkoy2y9lhtcl">
        <div data-id="element-1036" data-mp="1sktdsm2oik1r">
          <p className="text-sm text-ink-muted" data-id="element-1037" data-mp="1sk1sq56pk4pq">{quotes.length} tilbud i alt</p>
          <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-1038" data-mp="1wm6l70lf6vas">
            Tilbud
          </h1>
        </div>
        <Link to="/admin/tilbud/ny" className={buttonClasses('primary', 'md')} data-id="element-1039" data-mp="21j5drepfqu6v">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1040" data-mp="26irpfjpm82dk" />
          Opret tilbud
        </Link>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-id="element-1041" data-mp="2bbulbyhv7nwx">
        {cards.map(card => <div key={card.label} className="rounded-card border border-line bg-white p-4" data-id="element-1042" data-mp="2gbyt904pjabj">
            <p className="font-display text-2xl font-bold tracking-tight text-ink" data-id="element-1043" data-mp="2k9o5yc69ot49">{card.value}</p>
            <p className="mt-1 text-sm text-ink-muted" data-id="element-1044" data-mp="2p8rbryu2n3da">{card.label}</p>
          </div>)}
      </div>

      <div className="mt-6" data-id="element-1045" data-mp="dl4af444xg6e">
        <label htmlFor="tilbudsoegning" className="sr-only" data-id="element-1046" data-mp="8nw35ykiywjx">
          Søg i tilbud
        </label>
        <div className="flex items-center gap-2 rounded-xl border border-line bg-white px-4 py-3" data-id="element-1047" data-mp="4maivs5nvfzp">
          <SearchIcon className="h-4 w-4 shrink-0 text-ink-faint" aria-hidden="true" data-id="element-1048" data-mp="3vtmyw3ut5ne1" />
          <input id="tilbudsoegning" value={query} onChange={event => setQuery(event.target.value)} placeholder="Søg på tilbudsnummer, kunde eller titel" className="w-full bg-transparent text-[15px] text-ink placeholder:text-ink-faint focus:outline-none" data-id="element-1049" data-mp="wdqeuct3yf2l" />
        </div>

        <div className="mt-4 flex gap-2 overflow-x-auto pb-1" role="group" aria-label="Filtrér tilbud efter status" data-id="element-1050" data-mp="ilsbkim2vdec">
          {(['Alle', ...quoteStatuses] as Filter[]).map(option => {
          const active = filter === option;
          return <button key={option} type="button" onClick={() => setFilter(option)} aria-pressed={active} className={`whitespace-nowrap rounded-full border px-3.5 py-2 text-sm transition-colors duration-200 ease-smooth ${active ? 'border-ink bg-ink text-white' : 'border-line bg-white text-ink-soft hover:border-ink/30'}`} data-id="element-1051" data-mp="uuyivdzij58k">
                {option}
              </button>;
        })}
        </div>
      </div>

      {dataLoading && quotes.length === 0 && <p className="mt-8 flex items-center gap-2 text-[15px] text-ink-muted" data-id="element-1052" data-mp="14s349bljawob">
          <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1053" data-mp="19r5xlys5jegg" />
          Henter tilbud …
        </p>}

      <div className="mt-6 rounded-card border border-line bg-white" data-id="element-1054" data-mp="229bbjmiy6uuu">
        <div className="hidden md:block" data-id="element-1055" data-mp="22amexl8he0lf">
          <table className="w-full text-left text-[15px]" data-id="element-1056" data-mp="2bnvb3zd6jtyf">
            <thead data-id="element-1057" data-mp="2bmabvwfx7mu8">
              <tr className="border-b border-line text-xs uppercase tracking-wider text-ink-muted" data-id="element-1058" data-mp="27mi2ximm7eoc">
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1059" data-mp="2ldwy0ntsr6br">Tilbudsnummer</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1060" data-mp="2lg0xmtknxj68">Kunde</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1061" data-mp="2gfd9qgrqdig8">Titel</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1062" data-mp="1sqajikj16ha4">Dato</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1063" data-mp="1onxfc0jxmrfg">Gyldig til</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1064" data-mp="1oq0bl2bohgsm">Beløb</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1065" data-mp="22oqcyu5j62ms">Status</th>
                <th scope="col" className="px-5 py-3 font-medium text-right" data-id="element-1066" data-mp="1xnkjgu41qbc7">Handling</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line" data-id="element-1067" data-mp="3e4orvene3ivx">
              {visible.map(({
              quote,
              status
            }) => <tr key={quote.id} className="transition-colors duration-200 ease-smooth hover:bg-canvas" data-id="element-1068" data-mp="395vbnegpxds2">
                  <td className="whitespace-nowrap px-5 py-3.5 font-medium text-ink" data-id="element-1069" data-mp="3l5s9qpfrsnlb">{quote.quoteNumber}</td>
                  <td className="px-5 py-3.5 text-ink" data-id="element-1070" data-mp="3q54vf6uwnomb">{quote.customerName || '—'}</td>
                  <td className="px-5 py-3.5 text-ink-soft" data-id="element-1071" data-mp="3q324mklxhrgj">{quote.title}</td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-ink-soft" data-id="element-1072" data-mp="3v36o8ji4v2az">{formatDate(quote.quoteDate)}</td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-ink-soft" data-id="element-1073" data-mp="2xed34kgynk49">{formatDate(quote.validUntil)}</td>
                  <td className="whitespace-nowrap px-5 py-3.5 font-medium text-ink" data-id="element-1074" data-mp="37edrwlxlmsfu">{formatKr(quote.total)}</td>
                  <td className="px-5 py-3.5" data-id="element-1075" data-mp="3c4u6i9d5ok3s">
                    <QuoteStatusBadge status={status} data-id="element-1076" data-mp="3c6oopfx1ly46" />
                  </td>
                  <td className="px-5 py-3.5 text-right" data-id="element-1077" data-mp="2eooq46d2eod6">
                    <Link to={`/admin/tilbud/${quote.id}`} className={buttonClasses('secondary', 'sm')} data-id="element-1078" data-mp="2jot5ft28ld1t">
                      Åbn
                    </Link>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        <ul className="divide-y divide-line md:hidden" data-id="element-1079" data-mp="276ytvuux4klo">
          {visible.map(({
          quote,
          status
        }) => <li key={quote.id} data-id="element-1080" data-mp="22973x6du38k4">
              <Link to={`/admin/tilbud/${quote.id}`} className="block px-5 py-4" data-id="element-1081" data-mp="1x89lqtstqeim">
                <div className="flex items-start justify-between gap-3" data-id="element-1082" data-mp="1xadrzie73pm2">
                  <div className="min-w-0" data-id="element-1083" data-mp="1t7qub0uufagy">
                    <p className="text-xs text-ink-faint" data-id="element-1084" data-mp="1of61o2nob3f0">{quote.quoteNumber}</p>
                    <p className="mt-1 truncate text-[15px] font-medium text-ink" data-id="element-1085" data-mp="1jgmey0199nll">{quote.title}</p>
                    <p className="mt-0.5 truncate text-sm text-ink-muted" data-id="element-1086" data-mp="1ehjktamtd1se">{quote.customerName}</p>
                  </div>
                  <QuoteStatusBadge status={status} data-id="element-1087" data-mp="19iyh318crzz7" />
                </div>
                <p className="mt-2 text-sm text-ink-soft" data-id="element-1088" data-mp="15gtrcdl0znhu">
                  {formatDate(quote.quoteDate)} · {formatKr(quote.total)}
                </p>
              </Link>
            </li>)}
        </ul>

        {!dataLoading && visible.length === 0 && <p className="px-5 py-12 text-center text-sm text-ink-muted" data-id="element-1089" data-mp="2yc4a233aegm3">Ingen tilbud matcher søgningen.</p>}
      </div>
    </div>;
}